import { useState, useEffect, useRef, useMemo } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Play, Square, Pause } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { splitIntoChunks, pickDefaultVoice, scriptLanguage, listeningSeconds } from "./ttsUtils";

const TOOL_INFO_CONTENT = {
  what: `Converts written text into spoken audio using your browser's built-in speech synthesis, useful for proofreading by ear, accessibility, or just having text read aloud to you.`,
  how: `Paste your text, check the voice (it starts with one that matches your language), and click Speak. Pause or stop any time, or click a sentence to start from there.`,
  tip: `Hearing your own writing read aloud is a genuinely effective way to catch awkward phrasing that's easy to miss when just reading silently. The voices come from your own device and browser, not from this site; install more language/voice packs in your operating system's settings to get more here. Long text is read sentence by sentence so voices don't cut off. Text in another script (Hindi, Chinese, Arabic...) switches to a matching voice if your device has one. This tool speaks aloud only and can't save an audio file, because browsers don't expose the audio from this speech API.`,
};

const VOICE_KEY = "fdp-tts-voice";
const key = (v: SpeechSynthesisVoice) => `${v.name}|${v.lang}`;
const fmtTime = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`);

export default function TextToSpeech() {
  const [text, setText] = useState("");
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceKey, setVoiceKey] = useState("");
  const [rate, setRate] = useState([1]);
  const [pitch, setPitch] = useState([1]);
  const [volume, setVolume] = useState([1]);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [current, setCurrent] = useState(-1);
  const [voiceWaitDone, setVoiceWaitDone] = useState(false);
  const runRef = useRef(0); // invalidates callbacks of a queue that was stopped or restarted

  // Voices load asynchronously in most browsers; sorted so voices of one language sit together.
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) { setVoiceWaitDone(true); return; }
    // Some browsers fire no voiceschanged event when there are none; give them a moment, then say so.
    const wait = setTimeout(() => setVoiceWaitDone(true), 2000);
    const load = () => {
      const list = [...window.speechSynthesis.getVoices()].sort((a, b) => a.lang.localeCompare(b.lang) || a.name.localeCompare(b.name));
      if (list.length) { setVoices(list); setVoiceWaitDone(true); }
    };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => { clearTimeout(wait); window.speechSynthesis.removeEventListener?.("voiceschanged", load); window.speechSynthesis.cancel(); };
  }, []);

  // Default to a voice that matches the person's language (and remember what they pick), not the first in the list.
  useEffect(() => {
    if (voices.length === 0 || voiceKey) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(VOICE_KEY); } catch { /* storage unavailable */ }
    const savedVoice = saved ? voices.find((v) => key(v) === saved) : undefined;
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language || "en-US"];
    const pick = savedVoice ?? pickDefaultVoice(voices, langs);
    if (pick) setVoiceKey(key(pick));
  }, [voices, voiceKey]);

  const chunks = useMemo(() => splitIntoChunks(text), [text]);
  const { logUsageNow } = useLogUsageOnAction();
  const selected = voices.find((v) => key(v) === voiceKey);

  const voiceFor = (chunk: string): SpeechSynthesisVoice | undefined => {
    // Text in another writing system gets a matching voice when the device has one.
    const lang = scriptLanguage(chunk);
    if (lang && selected && selected.lang.toLowerCase().split("-")[0] !== lang) {
      const alt = pickDefaultVoice(voices.filter((v) => v.lang.toLowerCase().startsWith(lang)), [lang]);
      if (alt) return alt;
    }
    return selected;
  };

  const speakFrom = (start: number, run: number) => {
    if (run !== runRef.current) return;
    if (start >= chunks.length) { setSpeaking(false); setPaused(false); setCurrent(-1); return; }
    const u = new SpeechSynthesisUtterance(chunks[start]);
    u.rate = rate[0]; u.pitch = pitch[0]; u.volume = volume[0];
    const v = voiceFor(chunks[start]);
    if (v) { u.voice = v; u.lang = v.lang; }
    u.onstart = () => { if (run === runRef.current) setCurrent(start); };
    u.onend = () => speakFrom(start + 1, run);
    u.onerror = (e) => { if ((e as SpeechSynthesisErrorEvent).error === "canceled" || (e as SpeechSynthesisErrorEvent).error === "interrupted") return; speakFrom(start + 1, run); };
    window.speechSynthesis.speak(u);
  };

  const startAt = async (index: number, countUsage: boolean) => {
    if (!chunks.length) return;
    if (countUsage) { const allowed = await logUsageNow(); if (!allowed) return; }
    window.speechSynthesis.cancel();
    const run = ++runRef.current;
    setSpeaking(true); setPaused(false); setCurrent(index);
    speakFrom(index, run);
  };

  const stop = () => { runRef.current++; window.speechSynthesis.cancel(); setSpeaking(false); setPaused(false); setCurrent(-1); };
  const togglePause = () => {
    if (paused) { window.speechSynthesis.resume(); setPaused(false); }
    else { window.speechSynthesis.pause(); setPaused(true); }
  };
  const chooseVoice = (k: string) => { setVoiceKey(k); try { localStorage.setItem(VOICE_KEY, k); } catch { /* ignore */ } };

  return (
    <ToolContainer>
      <div className="space-y-4">
        <Textarea value={text} onChange={e => { setText(e.target.value); if (speaking) stop(); }} placeholder="Enter text to speak..." className="min-h-[150px]" />
        <p className="text-xs text-muted-foreground" data-testid="tts-count">{text.length.toLocaleString()} characters{text.trim() ? ` · about ${fmtTime(listeningSeconds(text, rate[0]))} to listen` : ""}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Voice</Label>
            <Select value={voiceKey} onValueChange={chooseVoice}>
              <SelectTrigger data-testid="tts-voice"><SelectValue placeholder="Default" /></SelectTrigger>
              <SelectContent>{voices.map((v) => <SelectItem key={key(v)} value={key(v)}>{v.name} ({v.lang})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Speed: {rate[0]}x</Label><Slider value={rate} onValueChange={setRate} min={0.5} max={2} step={0.1} /></div>
          <div><Label>Pitch: {pitch[0]}</Label><Slider value={pitch} onValueChange={setPitch} min={0.5} max={2} step={0.1} /></div>
          <div><Label>Volume: {Math.round(volume[0] * 100)}%</Label><Slider value={volume} onValueChange={setVolume} min={0} max={1} step={0.05} /></div>
        </div>
        {voiceWaitDone && voices.length === 0 && (
          <p role="alert" className="text-sm text-destructive" data-testid="tts-no-voices">
            Your browser has no speech voices, so Speak is unavailable. Try Chrome, Edge or Safari, or install a voice pack in your system's language or accessibility settings.
          </p>
        )}
        <div className="flex gap-3">
          {speaking ? (
            <>
              <Button variant="outline" onClick={togglePause} className="gap-2">{paused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}</Button>
              <Button variant="destructive" onClick={stop} className="gap-2"><Square className="h-4 w-4" /> Stop</Button>
            </>
          ) : (
            <Button onClick={() => startAt(0, true)} disabled={!text.trim() || !voices.length} className="gap-2"><Play className="h-4 w-4" /> Speak</Button>
          )}
        </div>
        {speaking && chunks.length > 1 && (
          <div className="rounded-lg border p-3 text-sm space-y-1 max-h-64 overflow-auto" data-testid="tts-reader">
            <p className="text-xs text-muted-foreground">Sentence {Math.max(current, 0) + 1} of {chunks.length}. Click a sentence to start from there.</p>
            {chunks.map((c, i) => (
              <button key={i} type="button" onClick={() => startAt(i, false)} className={`block w-full text-left rounded px-1.5 py-0.5 ${i === current ? "bg-primary/20 font-medium" : "hover:bg-muted"}`}>{c}</button>
            ))}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
